import { toaster } from '@/components/ui/toaster';
import {
  invalidateMentionBoostSetupQueries,
  updateMentionBoostSetupQueries,
} from '@/hooks/queries/use-mention-boost-setup-query';
import { invalidateCoPublicitesQueries } from '@/hooks/queries/use-co-publicites-query';
import api from '@/libs/axiosInstance';
import { getApiErrorMessage } from '@/libs/utils/error-utils';
import type {
  ApiErrorResponse,
  ApiResponse,
  MentionBoostDto,
  MentionBoostSetupResponse,
  StopMentionBoostResponse,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

interface StopMentionBoostVariables {
  mentionId: string;
}

const stopMentionBoost = async ({
  mentionId,
}: StopMentionBoostVariables): Promise<ApiResponse<StopMentionBoostResponse>> => {
  // The mention id in the path identifies the boost to stop; the body is empty.
  const response = await api.post<ApiResponse<StopMentionBoostResponse>>(
    `/dashboard/mentions/${mentionId}/boost-stop`,
    {}
  );

  return response.data;
};

/**
 * Stops a running mention boost. On success it primes the boost-setup cache with
 * the returned boost (delivery state `stopping`, or already `stopped`) so the
 * read-only delivery view flips immediately, then invalidates to reconcile with
 * the server. Stop failures are toasted through the shared `errors.<CODE>`
 * translation flow; the backend already returns marketer-safe messages.
 */
export const useStopMentionBoost = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse<StopMentionBoostResponse>,
    AxiosError<ApiErrorResponse>,
    StopMentionBoostVariables
  >({
    mutationFn: stopMentionBoost,
    onSuccess: (response, variables) => {
      const stoppedBoost: MentionBoostDto | null = response.data?.boost ?? null;

      if (stoppedBoost) {
        toaster.success({
          title: t('mentions.boost.stop.success'),
        });

        // Prime the boost-setup cache so the surface flips to "Stopping" right
        // away. Constructing the full shape keeps the boost from being dropped on
        // an undefined previous (cold cache), mirroring the launch hook.
        updateMentionBoostSetupQueries(
          queryClient,
          variables.mentionId,
          (previous) =>
            ({
              setup: previous?.setup ?? null,
              eligibility: previous?.eligibility,
              readiness: previous?.readiness ?? null,
              launchedBoost: stoppedBoost,
            }) as MentionBoostSetupResponse
        );
      }

      void invalidateMentionBoostSetupQueries(queryClient, variables.mentionId);
      void invalidateCoPublicitesQueries(queryClient);
    },
    onError: (error) => {
      toaster.error({
        title: t('common.error'),
        description: getApiErrorMessage(error, t, 'common.error'),
      });
    },
  });
};
